import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { nextBeforeAt } from '../../activities/useFetchMoreActivities';
import SVGIcon from '../../components/SVGIcon';
import Timestamp from '../../components/Timestamp';
import { BEACON_POST_ADD_OR_SHARE } from '../../constants/onboarding';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import addOrShareIcon from '../../icons/non-prefilled/16x16/addOrShare.svg';
import backlinksIcon from '../../icons/non-prefilled/16x16/backlinks.svg';
import TutorialBeacon from '../../onboarding/tutorial/TutorialBeacon';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';
import * as styles from './ControlBar.module.scss';
import ControlBarActivityButton from './ControlBarActivityButton';
import ControlBarAddOrSharePopOver from './ControlBarAddOrSharePopOver';
import ControlBarBacklinks from './ControlBarBacklinks';
import ControlBarButton from './ControlBarButton';
import ControlBarInsightsButton from './ControlBarInsightsButton';
import { ControlBarInsightsButtonContent } from './ControlBarInsightsButtonContent';
import ControlBarPopOver from './ControlBarPopOver';
import ControlBarSeriesButton from './ControlBarSeriesButton';
import ControlBarVerificationButton from './ControlBarVerificationButton';
import {
  controlBarInsightsStateAtom,
  isAddOrShareMenuOpenAtom,
} from './controlBarMenuAtoms';

const CONTROL_BAR_POST_QUERY = gql(/** @complexity 11 */ `
    query ControlBarPost($postId: ID!, $beforeAt: Datetime!) {
      post(id: $postId) {
        id
        ...ControlBarBacklinks_post
      }
      postActivities(postId: $postId, beforeAt: $beforeAt) {
        ...ControlBarActivityButton_activities
      }
    }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBar_post on Post {
    id
    ...ControlBarInsightsButtonContent_post
    ...ControlBarInsightsButton_post
    ...ControlBarAddOrSharePopOver_post
    ...ControlBarVerificationButton_post
    ...ControlBarActivityButton_post
  }
`);

const FOOTER_CONTROL_BAR_POST_FRAGMENT = gql(`
  fragment FooterControlBar_postDetails on Post {
    id
    archivedAt
    publishedAt
    ...ControlBarInsightsButton_post
  }
`);

const FOOTER_CONTROL_BAR_SERIES_FRAGMENT = gql(`
  fragment FooterControlBar_series on SeriesFields {
    id
    ...ControlBarSeriesButton_series
  }
`);

const FOOTER_CONTROL_BAR_SERIES_DETAILS_FRAGMENT = gql(`
  fragment FooterControlBar_seriesDetails on SeriesFields {
    id
    ...ControlBarSeriesButton_seriesDetails
  }
`);

interface FooterControlBarProps {
  postId: string;
  postDetails?: DocumentType<typeof FOOTER_CONTROL_BAR_POST_FRAGMENT>;
  series?: DocumentType<typeof FOOTER_CONTROL_BAR_SERIES_FRAGMENT> | null;
  seriesDetails?: DocumentType<
    typeof FOOTER_CONTROL_BAR_SERIES_DETAILS_FRAGMENT
  >;
}

export const FooterControlBar = ({
  postId,
  postDetails,
  series,
  seriesDetails,
}: FooterControlBarProps) => {
  return (
    <div className={styles.footerContainer}>
      <div className={styles.footerInsightsButtonWrapper}>
        {!!postDetails?.publishedAt && (
          <ControlBarInsightsButton
            popOverPlacement="top-end"
            post={postDetails}
          >
            {postDetails.archivedAt ? (
              <>
                Archived <Timestamp date={postDetails.archivedAt} />
              </>
            ) : (
              <>
                Published <Timestamp date={postDetails.publishedAt} />
              </>
            )}
          </ControlBarInsightsButton>
        )}
      </div>
      {series && (
        <ControlBarSeriesButton
          postId={postId}
          series={series}
          seriesDetails={seriesDetails}
        />
      )}
    </div>
  );
};

interface ControlBarProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  editing: boolean;
  disableInsightsButton?: boolean;
}

const ControlBar = ({
  post,
  editing,
  disableInsightsButton,
}: ControlBarProps) => {
  const [isBacklinksMenuOpen, setIsBacklinksMenuOpen] = useState(false);
  const [isActivityMenuOpen, setIsActivityMenuOpen] = useState(false);
  const [controlBarInsightsState, setControlBarInsightsState] = useAtom(
    controlBarInsightsStateAtom,
  );
  const [isAddOrShareMenuOpen, setIsAddOrShareMenuOpen] = useAtom(
    isAddOrShareMenuOpenAtom,
  );

  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const { data } = useQuery(CONTROL_BAR_POST_QUERY, {
    variables: {
      postId: post.id,
      beforeAt: nextBeforeAt(),
    },
    skip: isMobile,
  });

  const postWithBacklinks = data?.post;
  const postActivities = data?.postActivities;

  useEffect(() => {
    return () => {
      setIsBacklinksMenuOpen(false);
      setIsActivityMenuOpen(false);
      setControlBarInsightsState({ isOpen: false });
      setIsAddOrShareMenuOpen(false);
    };
  }, [
    post.id,
    setIsActivityMenuOpen,
    setIsAddOrShareMenuOpen,
    setIsBacklinksMenuOpen,
    setControlBarInsightsState,
  ]);

  useEffect(() => {
    if (isMobile && controlBarInsightsState.isOpen) {
      setControlBarInsightsState({
        isOpen: false,
      });
    }
  }, [isMobile, setControlBarInsightsState, controlBarInsightsState.isOpen]);

  useEffect(() => {
    if (isMobile && isBacklinksMenuOpen) {
      setIsBacklinksMenuOpen(false);
    }
  }, [isMobile, setIsBacklinksMenuOpen, isBacklinksMenuOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        {!disableInsightsButton && (
          <ControlBarInsightsButton
            state={controlBarInsightsState}
            onStateChange={setControlBarInsightsState}
            popOverPlacement="bottom-end"
            post={post}
          >
            <ControlBarInsightsButtonContent post={post} />
          </ControlBarInsightsButton>
        )}
        <ControlBarVerificationButton post={post} />
        {editing && (
          <ControlBarPopOver
            placement="bottom-end"
            open={isAddOrShareMenuOpen}
            onOpenChange={setIsAddOrShareMenuOpen}
            arrowClassName={styles.addOrShareArrow}
            content={
              <ControlBarAddOrSharePopOver
                onClose={() => setIsAddOrShareMenuOpen(false)}
                post={post}
              />
            }
          >
            <ControlBarButton
              active={isAddOrShareMenuOpen}
              icon={
                <ControlBarPopOver.Anchor>
                  <SVGIcon>{addOrShareIcon}</SVGIcon>
                </ControlBarPopOver.Anchor>
              }
            >
              <TutorialBeacon id={BEACON_POST_ADD_OR_SHARE} placement="top-end">
                <div>Add or share...</div>
              </TutorialBeacon>
            </ControlBarButton>
          </ControlBarPopOver>
        )}
      </div>

      <div className={styles.right}>
        {!isMobile &&
          postWithBacklinks &&
          postWithBacklinks.backlinks.filter(
            (backlink) => backlink.id !== postWithBacklinks.id,
          ).length > 0 && (
            <ControlBarPopOver
              content={<ControlBarBacklinks post={postWithBacklinks} />}
              open={isBacklinksMenuOpen}
              placement="bottom-start"
              onOpenChange={setIsBacklinksMenuOpen}
            >
              <ControlBarButton
                active={isBacklinksMenuOpen}
                icon={<SVGIcon>{backlinksIcon}</SVGIcon>}
              />
            </ControlBarPopOver>
          )}
        {!isMobile && postActivities && (
          <ControlBarActivityButton
            open={isActivityMenuOpen}
            setOpen={setIsActivityMenuOpen}
            post={post}
            activities={postActivities}
          />
        )}
      </div>
    </div>
  );
};

export default ControlBar;
